import { FlexSpinner } from 'components'
import { FormikEffectFactory } from 'components/FormikEffect'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  ResourceForm,
  ResourceFormSchema,
  ResourceFormValues,
} from 'forms/ResourceForm'
import {
  UsageTimespanForm,
  usageTimespanSchema,
} from 'forms/ResourceForm/UsageTimespanForm'
import { omitDeep } from 'helpers'
import {
  useCommand,
  useCreateCommand,
  useDeselectCommand,
  useEditCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import useSelection from 'hooks/useSelection'
import { pick } from 'lodash-es'
import {
  Participant,
  Proving,
  Resource,
  ResourceInsertType,
  ResourceSetType,
  UsageTimeSpan,
} from 'model'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import { FormType } from 'views/helpers'
import { useReadProvingsForResourceBookingOverviewQuery } from 'views/ResourceBookingsView/ResourceBookingsView.generated'
import {
  useCreateResourceMutation,
  useDeleteResourceMutation,
  useLiftingPlatformsQuery,
  useReadResourcesQuery,
  useUpdateResourceMutation,
} from './AdminResourcesListView.generated'
import { AdminResourcesProvingList } from './AdminResourcesProvingsListView'
import { RouteParams } from './AdminResourcesView'
import { ResourceList } from './ResourceList'

export interface AdminResourcesListViewProps {
  routeParams: RouteParams
  updateRouteParams: (updateParams: Partial<RouteParams>) => void
  filterState: string
  setFilterState: React.Dispatch<React.SetStateAction<string>>
  displayProvingItems: boolean
  setDisplayProvingItems: React.Dispatch<React.SetStateAction<boolean>>
}
export function addValueToKey(myMap: Map<any, any[]>, key: any, value: any) {
  myMap.set(key, myMap.get(key) || [])
  myMap.get(key)!.push(value)
}
const mapResourcesToProvings = (
  provings: any,
  type: string,
  category: string
): Map<any, any> => {
  const workstationsToProvings = new Map<string, string[]>()
  provings.forEach((proving: Proving) => {
    if (proving.resourceBookings) {
      proving.resourceBookings.forEach((resourceBooking: any) => {
        if (
          resourceBooking.resource &&
          resourceBooking.resource.type === type &&
          resourceBooking.resource.category === category
        ) {
          const provingData = {
            _id: proving._id,
            status: proving.status,
            dateFrom: proving.dateFrom,
            dateTo: proving.dateTo,
            id: proving.id,
            title: proving.title,
            provingGround: {
              title: proving.provingGround ? proving.provingGround.title : '',
            },
          }
          addValueToKey(
            workstationsToProvings,
            resourceBooking.resource._id,
            provingData
          )
        }
      })
    }
  })
  return workstationsToProvings
}
const mapResourcesToProvingsWithoutType = (
  provings: any,
  category: string
): Map<any, any> => {
  const resourcesToProvings = new Map<string, string[]>()
  provings.forEach((proving: Proving) => {
    if (proving.resourceBookings) {
      proving.resourceBookings.forEach((resourceBooking: any) => {
        if (
          resourceBooking.resource &&
          resourceBooking.resource.category === category
        ) {
          const provingData = {
            _id: proving._id,
            status: proving.status,
            dateFrom: proving.dateFrom,
            dateTo: proving.dateTo,
            id: proving.id,
            title: proving.title,
            provingGround: {
              title: proving.provingGround ? proving.provingGround.title : '',
            },
          }
          addValueToKey(
            resourcesToProvings,
            resourceBooking.resource._id,
            provingData
          )
        }
      })
    }
  })
  return resourcesToProvings
}

const mapResourcesToProvingParticipants = (
  provings: any,
  category: string,
  type?: string
): Map<any, any> => {
  const participantResourcesToProvings = new Map<string, string[]>()
  provings.forEach((proving: Proving) => {
    if (category === 'lodging') {
      if (proving.participants) {
        proving.participants.forEach((participant: Participant) => {
          if (
            participant.accomodation &&
            participant.accomodation.accomodation
          ) {
            const provingData = {
              _id: proving._id,
              status: proving.status,
              dateFrom: proving.dateFrom,
              dateTo: proving.dateTo,
              id: proving.id,
              title: proving.title,
              provingGround: {
                title: proving.provingGround ? proving.provingGround.title : '',
              },
            }
            addValueToKey(
              participantResourcesToProvings,
              participant.accomodation.accomodation._id,
              provingData
            )
          }
        })
      }
    } else if (category === 'ferryConnection') {
      if (proving.participants) {
        proving.participants.forEach((participant: Participant) => {
          if (
            (participant.ferry && participant.ferry.outward) ||
            (participant.ferry && participant.ferry.return)
          ) {
            const provingData = {
              _id: proving._id,
              status: proving.status,
              dateFrom: proving.dateFrom,
              dateTo: proving.dateTo,
              id: proving.id,
              title: proving.title,
              provingGround: {
                title: proving.provingGround ? proving.provingGround.title : '',
              },
            }
            if (participant.ferry.return && type === 'return') {
              addValueToKey(
                participantResourcesToProvings,
                participant.ferry.return._id,
                provingData
              )
            }
            if (participant.ferry.outward && type === 'outward') {
              addValueToKey(
                participantResourcesToProvings,
                participant.ferry.outward._id,
                provingData
              )
            }
          }
        })
      }
    }
  })
  return participantResourcesToProvings
}

export const AdminResourcesListView: React.FunctionComponent<
  AdminResourcesListViewProps
> = ({
  routeParams: { pgid, category, type, search, parentID, provingsList, id },
  updateRouteParams,
  filterState,
  setFilterState,
  displayProvingItems,
  setDisplayProvingItems,
}) => {
  const { t } = useTranslation()

  const [createResource] = useCreateResourceMutation()
  const [updateResource] = useUpdateResourceMutation()
  const [deleteResource] = useDeleteResourceMutation()

  const router = useRouter()
  const [formType, setFormType] = React.useState<FormType>()
  const [selectedItemId, setSelectedItemId] = React.useState<string | null>()
  const [provingListItems, setProvingListItems] = React.useState<any>()

  const { loading, data, error, refetch } = useReadResourcesQuery({
    variables: {
      search,
      rootId: pgid,
      category,
      type: type === 'all' ? undefined : type,
    },
  })

  const items =
    data && data.resources ? data.resources.filter((x) => !x._deleted) : []
  const [selection, selectedItems] = useSelection<Resource>({
    getKey: (item) => item._id,
  })

  const { loading: lpLoading, data: lpData } = useLiftingPlatformsQuery({
    variables: {
      rootId: pgid,
    },
  })
  const liftingPlatforms = lpData && lpData.resources ? lpData.resources : []

  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [
    usageTimeSpanPanelState,
    showUsageTimespanPanel,
    hideUsageTimespanPanel,
  ] = useFormikPanelState()
  const [dialogState, , hideDialog] = useDialogState()

  const { data: provingsData, loading: provingsLoading } =
    useReadProvingsForResourceBookingOverviewQuery()
  const resourceBookings =
    provingsData && provingsData.provings
      ? mapResourcesToProvings(provingsData.provings, 'workstation', 'office')
      : undefined
  const coldCellBookings =
    provingsData && provingsData.provings
      ? mapResourcesToProvingsWithoutType(provingsData.provings, 'coldCell')
      : undefined
  const refuellingBookings =
    provingsData && provingsData.provings
      ? mapResourcesToProvingsWithoutType(provingsData.provings, 'refuelling')
      : undefined
  const fleetBookings =
    provingsData && provingsData.provings
      ? mapResourcesToProvingsWithoutType(provingsData.provings, 'fleet')
      : undefined
  const ferryConnectionOutwardBookings =
    provingsData && provingsData.provings
      ? mapResourcesToProvingParticipants(
          provingsData.provings,
          'ferryConnection',
          'outward'
        )
      : undefined
  const ferryConnectionReturnBookings =
    provingsData && provingsData.provings
      ? mapResourcesToProvingParticipants(
          provingsData.provings,
          'ferryConnection',
          'return'
        )
      : undefined
  const testTrackUsageBookings =
    provingsData && provingsData.provings
      ? mapResourcesToProvings(
          provingsData.provings,
          'testTrackUsage',
          'testing'
        )
      : undefined
  const accessoryBookings =
    provingsData && provingsData.provings
      ? mapResourcesToProvingsWithoutType(provingsData.provings, 'accessory')
      : undefined
  const liftingPlatformBookings =
    provingsData && provingsData.provings
      ? mapResourcesToProvings(
          provingsData.provings,
          'liftingPlatform',
          'workshop'
        )
      : undefined
  const garageBookings =
    provingsData && provingsData.provings
      ? mapResourcesToProvings(provingsData.provings, 'garage', 'workshop')
      : undefined
  const accomodationBookings =
    provingsData && provingsData.provings
      ? mapResourcesToProvingParticipants(provingsData.provings, 'lodging')
      : undefined
  function getItems() {
    if (filterState) {
      return items.filter(
        (item) => item.parent && item.parent._id === filterState
      )
    } else {
      return items
    }
  }
  useEffect(() => {
    if (parentID && !provingsList) {
      setFilterState(parentID)
    }
    if (provingsList && !provingsLoading) {
      setDisplayProvingItems(true)
      let returnValue: any[] = []
      const generalResourceBookings =
        provingsData && provingsData.provings
          ? mapResourcesToProvings(provingsData.provings, type, category)
          : undefined

      if (generalResourceBookings && generalResourceBookings.has(id)) {
        returnValue = generalResourceBookings.get(id)
      } else {
        returnValue = []
      }
      setProvingListItems(returnValue)
    }
  }, [
    parentID,
    category,
    id,
    setDisplayProvingItems,
    setFilterState,
    type,
    provingsList,
    provingsData,
    provingsLoading,
  ])
  const handleSubmit = async (
    values: ResourceFormValues,
    formikActions: FormikActions<ResourceFormValues>
  ) => {
    formikActions.setSubmitting(true)

    if (formType === FormType.update) {
      await updateResource({
        variables: {
          _id: selectedItemId,
          update: omitDeep(values, ['parent']) as ResourceSetType,
        },
      })
      if (values.category === 'workshop' && values.usageTimespans) {
        const lpFiltered = liftingPlatforms.filter(
          (lp) => lp.parentId === values._id
        )
        for (const lp of lpFiltered) {
          await updateResource({
            variables: {
              _id: lp._id,
              update: {
                external: true,
                usageTimespans: values.usageTimespans,
              },
            },
          })
        }
      }
    } else {
      const queryResult = await createResource({
        variables: { input: values as ResourceInsertType },
      })
      const createdResource =
        queryResult.data && queryResult.data.createResource
          ? queryResult.data.createResource
          : ({} as Resource)
      if (
        createdResource.rootId !== pgid ||
        createdResource.category !== category ||
        (createdResource.type !== type && type !== 'all')
      ) {
        updateRouteParams({
          pgid: createdResource.rootId || undefined,
          category: createdResource.category || undefined,
          type: createdResource.type,
        })
      } else {
        refetch({
          search: undefined,
          rootId: pgid,
          category,
          type: type === 'all' ? undefined : type,
        })
      }
    }
    formikActions.setSubmitting(false)
    selection.setAllSelected(false)
    setSelectedItemId(null)
    refetch()
    hidePanel()
  }

  const handleConfirmDelete = async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((x) => x._id)

      await deleteResource({
        variables: { ids },
      })
    }
    hideDialog()
    refetch({
      search: undefined,
      rootId: pgid,
      category,
      type: type === 'all' ? undefined : type,
    })
  }

  const handleUsageTimespanSubmit = async (
    values: { external: boolean; usageTimespans: UsageTimeSpan[] },
    formikActions: FormikActions<any>
  ) => {
    formikActions.setSubmitting(true)

    for (const resource of selectedItems) {
      await updateResource({
        variables: {
          _id: resource._id,
          update: {
            external: values.external,
            usageTimespans: values.usageTimespans,
          },
        },
      })
      if (category === 'workshop' && values.usageTimespans) {
        const lpFiltered = liftingPlatforms.filter(
          (lp) => lp.parentId === resource._id
        )
        for (const lp of lpFiltered) {
          await updateResource({
            variables: {
              _id: lp._id,
              update: {
                external: values.external,
                usageTimespans: values.usageTimespans,
              },
            },
          })
        }
      }
    }
    refetch()
    formikActions.setSubmitting(false)
    hideUsageTimespanPanel()
  }

  const [validationSchema, setValidationSchema] = React.useState(
    new ResourceFormSchema(category, type)
  )
  const UpdateValidationSchemaEffect = React.useCallback(
    FormikEffectFactory<ResourceFormValues>((state, nextState) => {
      if (
        state.values.category !== nextState.values.category ||
        state.values.type !== nextState.values.type
      ) {
        setValidationSchema(
          new ResourceFormSchema(
            nextState.values.category || '',
            nextState.values.type || ''
          )
        )
      }
    }),
    [setValidationSchema]
  )

  /** commands */
  useCreateCommand(
    'resource',
    {
      priority: 10,
      onClick: () => {
        setFormType(FormType.insert)
        showPanel({
          rootId: pgid,
          category,
          type: type === 'all' ? '' : type,
          external: false,
          usageTimespans: [] as UsageTimeSpan[],
        } as ResourceInsertType)
      },
    },
    [pgid, category, type]
  )

  useEditCommand(
    { name: 'resource', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        setFormType(FormType.update)
        setSelectedItemId(selectedItems[0]._id)
        showPanel({
          ...omitDeep(selectedItems[0], ['__typename']),
          notification: '',
          usageTimespans: selectedItems[0].usageTimespans || [],
          external: selectedItems[0].external || false,
        })
      },
    },
    [selectedItems]
  )

  // const isSameTimespans = React.useCallback(() => {
  //   if (selectedItems.length <= 1) {
  //     return true
  //   }
  //   let isSame = true
  //   const firstItem = selectedItems[0]
  //   for (const res of selectedItems) {
  //     if (res.usageTimespans && firstItem.usageTimespans) {
  //       const ut2 = firstItem.usageTimespans
  //       res.usageTimespans.forEach((ut1, i) => {
  //         if (
  //           !(
  //             ut2[i] &&
  //             ut1.dateFrom === ut2[i].dateFrom &&
  //             ut1.dateTo === ut2[i].dateTo
  //           )
  //         ) {
  //           isSame = false
  //         }
  //       })
  //     }
  //   }
  //   return isSame
  // }, [selectedItems])

  useCommand(
    {
      key: 'editUsageTimespans',
      text: 'Nutzungszeiträume',
      priority: 30,
      iconProps: { iconName: 'calendar' },
      disabled: selectedItems.length < 1,
      onClick: () =>
        showUsageTimespanPanel({
          external:
            selectedItems.length > 1 ? false : selectedItems[0].external,
          usageTimespans:
            selectedItems.length > 1
              ? []
              : omitDeep(selectedItems[0].usageTimespans, ['__typename']),
        }),
    },
    [selectedItems, showUsageTimespanPanel]
  )
  // useDeleteCommand(
  //   { name: 'resource', count: selectedItems.length },
  //   {
  //     priority: 30,
  //     onClick: showDialog,
  //   },
  //   [selectedItems]
  // )

  /** farCommands */
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 10,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )

  // router.navigate('admin.data.resources', {
  //   pgid: proving!.provingGround!._id,
  //   id: initialValues.resource,
  //   provingsList: true,
  //   category: initialValues.category,
  //   type: initialValues.type,
  // })

  return (
    <FlexSpinner loading={loading || lpLoading} error={error}>
      {displayProvingItems && (
        <AdminResourcesProvingList
          items={provingListItems}
          onItemInvoked={(item) =>
            router.navigate('provings.details', {
              id: item.id,
              page: 'resourceBookings',
            })
          }
          selection={selection}
        />
      )}
      {!displayProvingItems && (
        <ResourceList
          items={getItems()}
          category={category}
          type={type}
          selection={selection}
          onItemInvoked={(item) => {
            if (category === 'building') {
              updateRouteParams({
                pgid,
                category: 'office',
                type: 'officeRoom',
                search: '',
              })
              setFilterState(item._id)
            } else if (category === 'office' && type === 'officeRoom') {
              updateRouteParams({
                pgid,
                category: 'office',
                type: 'workstation',
                search: '',
              })
              setFilterState(item._id)
            } else if (category === 'office' && type === 'workstation') {
              setDisplayProvingItems(true)
              let returnValue: any[] = []
              if (resourceBookings && resourceBookings.has(item._id)) {
                returnValue =
                  resourceBookings && selectedItems[0]
                    ? resourceBookings.get(selectedItems[0]._id)
                    : undefined
              } else {
                returnValue = []
                // setFormType(FormType.update)
                // setSelectedItemId(selectedItems[0]._id)
                // showPanel(omitDeep(selectedItems[0], ['__typename']))
              }
              setProvingListItems(returnValue)
            } else if (category === 'coldCell') {
              setDisplayProvingItems(true)
              let returnValue: any[] = []
              if (coldCellBookings && coldCellBookings.has(item._id)) {
                returnValue =
                  coldCellBookings && selectedItems[0]
                    ? coldCellBookings.get(selectedItems[0]._id)
                    : undefined
              } else {
                returnValue = []
              }
              setProvingListItems(returnValue)
            } else if (category === 'refuelling') {
              setDisplayProvingItems(true)
              let returnValue: any[] = []
              if (refuellingBookings && refuellingBookings.has(item._id)) {
                returnValue =
                  refuellingBookings && selectedItems[0]
                    ? refuellingBookings.get(selectedItems[0]._id)
                    : undefined
              } else {
                returnValue = []
              }
              setProvingListItems(returnValue)
            } else if (category === 'fleet') {
              setDisplayProvingItems(true)
              let returnValue: any[] = []
              if (fleetBookings && fleetBookings.has(item._id)) {
                returnValue =
                  fleetBookings && selectedItems[0]
                    ? fleetBookings.get(selectedItems[0]._id)
                    : undefined
              } else {
                returnValue = []
              }
              setProvingListItems(returnValue)
            } else if (
              category === 'ferryConnection' &&
              item.type === 'outward'
            ) {
              setDisplayProvingItems(true)
              let returnValue: any[] = []
              if (
                ferryConnectionOutwardBookings &&
                ferryConnectionOutwardBookings.has(item._id)
              ) {
                returnValue =
                  ferryConnectionOutwardBookings && selectedItems[0]
                    ? ferryConnectionOutwardBookings.get(selectedItems[0]._id)
                    : undefined
              } else {
                returnValue = []
              }
              setProvingListItems(returnValue)
            } else if (
              category === 'ferryConnection' &&
              item.type === 'return'
            ) {
              setDisplayProvingItems(true)
              let returnValue: any[] = []
              if (
                ferryConnectionReturnBookings &&
                ferryConnectionReturnBookings.has(item._id)
              ) {
                returnValue =
                  ferryConnectionReturnBookings && selectedItems[0]
                    ? ferryConnectionReturnBookings.get(selectedItems[0]._id)
                    : undefined
              } else {
                returnValue = []
              }
              setProvingListItems(returnValue)
            } else if (category === 'testing') {
              setDisplayProvingItems(true)
              let returnValue: any[] = []
              if (
                testTrackUsageBookings &&
                testTrackUsageBookings.has(item._id)
              ) {
                returnValue =
                  testTrackUsageBookings && selectedItems[0]
                    ? testTrackUsageBookings.get(selectedItems[0]._id)
                    : undefined
              } else {
                returnValue = []
              }
              setProvingListItems(returnValue)
            } else if (category === 'accessory') {
              setDisplayProvingItems(true)
              let returnValue: any[] = []
              if (accessoryBookings && accessoryBookings.has(item._id)) {
                returnValue =
                  accessoryBookings && selectedItems[0]
                    ? accessoryBookings.get(selectedItems[0]._id)
                    : undefined
              } else {
                returnValue = []
              }
              setProvingListItems(returnValue)
            } else if (
              category === 'workshop' &&
              item.type === 'liftingPlatform'
            ) {
              setDisplayProvingItems(true)
              let returnValue: any[] = []
              if (
                liftingPlatformBookings &&
                liftingPlatformBookings.has(item._id)
              ) {
                returnValue =
                  liftingPlatformBookings && selectedItems[0]
                    ? liftingPlatformBookings.get(selectedItems[0]._id)
                    : undefined
              } else {
                returnValue = []
              }
              setProvingListItems(returnValue)
            } else if (category === 'workshop' && item.type === 'garage') {
              setDisplayProvingItems(true)
              let returnValue: any[] = []
              if (garageBookings && garageBookings.has(item._id)) {
                returnValue =
                  garageBookings && selectedItems[0]
                    ? garageBookings.get(selectedItems[0]._id)
                    : undefined
              } else {
                returnValue = []
              }
              setProvingListItems(returnValue)
            } else if (category === 'lodging') {
              setDisplayProvingItems(true)
              let returnValue: any[] = []
              if (accomodationBookings && accomodationBookings.has(item._id)) {
                returnValue =
                  accomodationBookings && selectedItems[0]
                    ? accomodationBookings.get(selectedItems[0]._id)
                    : undefined
              } else {
                returnValue = []
              }
              setProvingListItems(returnValue)
            } else {
              setFormType(FormType.update)
              setSelectedItemId(selectedItems[0]._id)
              showPanel(omitDeep(selectedItems[0], ['__typename']))
            }
            // selectedItems.length === 1
            //   ? (() => {
            //       setFormType(FormType.update)
            //       setSelectedItemId(selectedItems[0]._id)
            //       showPanel(omitDeep(selectedItems[0], ['parent', '__typename']))
            //     })()
            //   : false
          }}
        />
      )}
      <FormikPanel
        {...panelState}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        headerText={
          panelState.initialValues && panelState.initialValues._id
            ? t('model.resource.edit')
            : t('model.resource.create')
        }
      >
        <UpdateValidationSchemaEffect />
        <ResetFormEffect />
        <ResourceForm />
      </FormikPanel>
      <FormikPanel
        {...usageTimeSpanPanelState}
        onSubmit={handleUsageTimespanSubmit}
        validationSchema={usageTimespanSchema}
        headerText={t('model.resource.edit')}
      >
        <UsageTimespanForm />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const ResetFormEffect: React.FunctionComponent =
  FormikEffectFactory<ResourceFormValues>(
    (formikState, nextFormikState, actions) => {
      if (formikState.values.category !== nextFormikState.values.category) {
        actions.resetForm(
          pick(nextFormikState.values, [
            '_id',
            '__typename',
            'rootId',
            'category',
            'title',
          ]) as ResourceFormSchema
        )
      }
    }
  )
